import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type InventorySalesChannelsFlatTeamSettingGroupId =
  | "inventory_sales_channels.shopify"
  | "inventory_sales_channels.amazon";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.ims",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "high",
  reason:
    "Pushing sets the quantities shoppers can buy on the sales channel, and an import overwrites Redo's on-hand at a location.",
};

export const InventorySalesChannelsFlatTeamSettingGroups = [
  {
    id: "inventory_sales_channels.shopify",
    searchLabels: ["Push inventory to Shopify", "Import from Shopify"],
    title: "Shopify inventory",
    description:
      "Keeps Shopify's available quantities in sync with Redo, and imports a location's on-hand counts from Shopify into Redo one location at a time.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "inventory-management/sales-channels",
    },
    synonyms: [
      "shopify inventory push",
      "import inventory from shopify",
      "sales channel inventory",
    ],
  },
  {
    id: "inventory_sales_channels.amazon",
    searchLabels: ["Push inventory to Amazon"],
    title: "Amazon inventory",
    description:
      "Lets Redo set Amazon's available quantity for a product's merchant-fulfilled offers when its inventory is pushed from the product's Inventory tab.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "inventory-management/sales-channels",
    },
    synonyms: ["amazon inventory push", "amazon available quantity"],
  },
] as const satisfies readonly SettingGroupDefinition<InventorySalesChannelsFlatTeamSettingGroupId>[];
